import React, { useState, useEffect } from "react";
import { Box, Container, Grid, Card, CardContent, Typography, CircularProgress } from "@mui/material";
import { ShoppingCart, LocalShipping, CheckCircle, PendingActions, Inventory } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getStats } from "../services/api";
import dayjs from "dayjs";

const Dashboard = () => {
	const [stats, setStats] = useState(null);
	const [loading, setLoading] = useState(true);
	const navigate = useNavigate();

	useEffect(() => {
		fetchStats();
	}, []);

	const fetchStats = async () => {
		try {
			const response = await getStats();
			setStats(response.data);
		} catch (error) {
			console.error("Error fetching stats:", error);
		} finally {
			setLoading(false);
		}
	};

	// Get date 30 days ago
	const getThirtyDaysAgo = () => {
		return dayjs().subtract(30, 'days').format('YYYY-MM-DD');
	};

	// Handle card click - navigate to sales order list with filters
	const handleCardClick = (filterType) => {
		const thirtyDaysAgo = getThirtyDaysAgo();
		const today = dayjs().format('YYYY-MM-DD');
		
		const params = new URLSearchParams();
		params.append('start_date', thirtyDaysAgo);
		params.append('end_date', today);
		
		// Add barcode_status filter based on the card clicked
		if (filterType === 'dispatched') {
			params.append('barcode_status', 'Dispatched');
		} else if (filterType === 'pending') {
			params.append('barcode_status', 'Pending to dispatch');
		} else if (filterType === 'dispatched_delivered') {
			// For delivered orders, filter by both dispatched and having GRC
			params.append('barcode_status', 'Dispatched');
			// Note: GRC filter would need to be handled separately or combined
		} else if (filterType === 'dispatched_pending') {
			// For pending delivery, filter by dispatched but not delivered
			params.append('barcode_status', 'Dispatched');
			// Note: This would need additional logic to exclude delivered orders
		}
		// For 'total', no additional filter - just show all orders in date range
		
		// Navigate to sales orders list with date range and barcode status filter
		navigate(`/explore/sora/sales-orders?${params.toString()}`);
	};

	const StatCard = ({ title, value, icon, onClick, subtitle }) => (
		<Card
			onClick={onClick}
			elevation={0}
			sx={{
				height: "100%",
				border: "1px solid",
				borderColor: "divider",
				borderRadius: 3,
				transition: "all 0.3s ease",
				cursor: onClick ? "pointer" : "default",
				"&:hover": {
					transform: onClick ? "translateY(-2px)" : "none",
					boxShadow: onClick ? 2 : 0,
					borderColor: onClick ? "primary.main" : "divider",
				},
			}}>
			<CardContent sx={{ p: 4 }}>
				<Box display="flex" flexDirection="column" gap={2}>
					<Box
						sx={{
							width: 48,
							height: 48,
							borderRadius: 2,
							backgroundColor: "rgba(0, 0, 0, 0.05)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							mb: 1,
							color: "text.primary",
							fontSize: "1.5rem",
						}}>
						{icon}
					</Box>

					<Box>
						<Typography
							color="text.secondary"
							variant="body2"
							sx={{
								fontSize: "0.8125rem",
								fontWeight: 400,
								mb: 0.5,
								textTransform: "uppercase",
								letterSpacing: "0.05em",
							}}>
							{title}
						</Typography>
						
						{subtitle && (
							<Typography
								color="text.secondary"
								variant="caption"
								sx={{
									fontSize: "0.75rem",
									mb: 1,
									display: "block",
								}}>
								{subtitle}
							</Typography>
						)}

						<Typography
							variant="h3"
							component="div"
							sx={{
								fontWeight: 600,
								fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
								letterSpacing: "-0.02em",
								lineHeight: 1.1,
							}}>
							{value?.toLocaleString() || "0"}
						</Typography>
					</Box>
				</Box>
			</CardContent>
		</Card>
	);

	if (loading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: "400px",
				}}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
			<Box sx={{ mb: { xs: 4, sm: 6, md: 8 } }}>
				<Typography
					variant="h1"
					sx={{
						fontSize: { xs: "2rem", sm: "2.5rem", md: "3.5rem" },
						fontWeight: 600,
						letterSpacing: "-0.02em",
						mb: 2,
						lineHeight: 1.1,
					}}>
					Sales Order
				</Typography>

				<Typography
					variant="h1"
					sx={{
						fontSize: { xs: "2rem", sm: "2.5rem", md: "3.5rem" },
						fontWeight: 600,
						letterSpacing: "-0.02em",
						lineHeight: 1.1,
					}}>
					Tracking
				</Typography>

				<Typography
					variant="body1"
					color="text.secondary"
					sx={{
						mt: 2,
						fontSize: "1.125rem",
						maxWidth: "600px",
					}}>
					Monitor your sales orders from creation to delivery with real-time tracking and TAT analysis.
				</Typography>
			</Box>

			<Box sx={{ mb: 4 }}>
				<Typography
					variant="h5"
					sx={{
						fontWeight: 600,
						mb: 3,
						fontSize: "1.5rem",
					}}>
					Last 30 Days Summary
				</Typography>

			<Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
					<Grid item xs={12} sm={6} md={4}>
						<StatCard 
							title="Total SO Created" 
							value={stats?.total_created} 
							icon={<ShoppingCart />}
							onClick={() => handleCardClick('total')}
							subtitle="Last 30 days"
						/>
				</Grid>

					<Grid item xs={12} sm={6} md={4}>
						<StatCard 
							title="Dispatched" 
							value={stats?.total_dispatched} 
							icon={<LocalShipping />}
							onClick={() => handleCardClick('dispatched')}
							subtitle={`Out of ${stats?.total_created || 0} created`}
						/>
				</Grid>

					<Grid item xs={12} sm={6} md={4}>
						<StatCard 
							title="Pending" 
							value={stats?.total_pending} 
							icon={<PendingActions />}
							onClick={() => handleCardClick('pending')}
							subtitle="Not yet dispatched"
						/>
					</Grid>
				</Grid>
			</Box>

			<Box sx={{ mb: 4 }}>
				<Typography
					variant="h5"
					sx={{
						fontWeight: 600,
						mb: 3,
						fontSize: "1.5rem",
					}}>
					Dispatched Orders Status
				</Typography>
				
				<Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
					<Grid item xs={12} sm={6} md={6}>
						<StatCard 
							title="Delivered" 
							value={stats?.dispatched_delivered} 
							icon={<CheckCircle />}
							onClick={() => handleCardClick('dispatched_delivered')}
							subtitle={`Out of ${stats?.total_dispatched || 0} dispatched`}
						/>
				</Grid>

					<Grid item xs={12} sm={6} md={6}>
						<StatCard 
							title="Pending Delivery" 
							value={stats?.dispatched_pending} 
							icon={<Inventory />}
							onClick={() => handleCardClick('dispatched_pending')}
							subtitle="In transit or awaiting delivery"
						/>
				</Grid>
			</Grid>
			</Box>

			{/* Brand Level Summary */}
			{stats?.by_brand && stats.by_brand.length > 0 && (
				<Box sx={{ mb: 4, mt: 8 }}>
					<Typography
						variant="h5"
						sx={{
							fontWeight: 600,
							mb: 3,
							mt: 3,
							fontSize: "1.5rem",
						}}>
						Summary by Brand
					</Typography>
					
					<Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
						{stats.by_brand.slice(0, 6).map((brandStat, index) => (
							<Grid item xs={12} sm={6} md={4} key={index}>
								<Card
									elevation={0}
									sx={{
										height: "100%",
										display: "flex",
										flexDirection: "column",
										border: "1px solid",
										borderColor: "divider",
										borderRadius: 3,
										p: 3,
										minHeight: "180px",
									}}>
									<Typography
										variant="h6"
										sx={{
											fontWeight: 600,
											mb: 2,
											fontSize: "1rem",
											color: "text.primary",
										}}>
										{brandStat.brand || "Unknown"}
									</Typography>
									<Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
										<Typography variant="body2" color="text.secondary">
											Created: <strong>{brandStat.total_created?.toLocaleString() || 0}</strong>
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Dispatched: <strong>{brandStat.total_dispatched?.toLocaleString() || 0}</strong>
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Pending: <strong>{brandStat.total_pending?.toLocaleString() || 0}</strong>
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Delivered: <strong>{brandStat.dispatched_delivered?.toLocaleString() || 0}</strong>
										</Typography>
									</Box>
								</Card>
							</Grid>
						))}
					</Grid>
				</Box>
			)}

			{/* Channel Level Summary */}
			{stats?.by_channel && stats.by_channel.length > 0 && (
				<Box sx={{ mb: 4, mt: 8 }}>
					<Typography
						variant="h5"
						sx={{
							fontWeight: 600,
							mb: 3,
							mt: 3,
							fontSize: "1.5rem",
						}}>
						Summary by Channel
					</Typography>
					
					<Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
						{stats.by_channel.slice(0, 6).map((channelStat, index) => (
							<Grid item xs={12} sm={6} md={4} key={index}>
								<Card
									elevation={0}
									sx={{
										height: "100%",
										display: "flex",
										flexDirection: "column",
										border: "1px solid",
										borderColor: "divider",
										borderRadius: 3,
										p: 3,
										minHeight: "180px",
									}}>
									<Typography
										variant="h6"
										sx={{
											fontWeight: 600,
											mb: 2,
											fontSize: "1rem",
											color: "text.primary",
										}}>
										{channelStat.channel || "Unknown"}
									</Typography>
									<Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
										<Typography variant="body2" color="text.secondary">
											Created: <strong>{channelStat.total_created?.toLocaleString() || 0}</strong>
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Dispatched: <strong>{channelStat.total_dispatched?.toLocaleString() || 0}</strong>
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Pending: <strong>{channelStat.total_pending?.toLocaleString() || 0}</strong>
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Delivered: <strong>{channelStat.dispatched_delivered?.toLocaleString() || 0}</strong>
										</Typography>
									</Box>
								</Card>
							</Grid>
						))}
					</Grid>
				</Box>
			)}
		</Container>
	);
};

export default Dashboard;
